/* Church Sign Shop — product configurator.
   Sides/shape/variant mapping + two self-serve design paths that both end in
   a press-ready PDF generated in the browser (no proof loop, no backend):

   1. Template path (layer model) — BG color (customer) → design-layer PNG
      (shop-supplied full-sheet transparent PNG, white art only; customer
      flips it white/black) → logo + #hashtag footer (customer).
      The preview shows the finished sign (trim crop, rounded corners); the
      print file carries the full sheet with bleed + marks.
   2. Upload path — JPG/PNG only, preflighted in the browser (effective DPI vs
      physical sign + bleed, crop preview) and self-approved by the customer.

   On add-to-cart the design is rendered at print DPI, wrapped in a PDF
   (SignPDF.build) and attached to the order as a file line-item property;
   the design JSON rides along for re-rendering. */
(function () {
  "use strict";

/* Re-runnable init: the Shopify theme editor re-renders the section's HTML
   on every setting/block change without re-running scripts, so we re-init on
   shopify:section:load. A data flag keeps a given DOM from double-binding. */
function initConfigurator() {
  var root = document.querySelector("[data-pdp]");
  if (!root || root.getAttribute("data-cs-init") === "1") return;
  root.setAttribute("data-cs-init", "1");
  var ST = window.SignTemplates;
  if (!ST) { console.error("Configurator: sign-templates.js missing"); return; }

  /* ── Config (variants + print specs from the section's JSON block) ──
     Anchor-mark defaults measured from the shop's own blank templates
     (Templates - Blank/Greeting Sign *): 0.451" white square, 0.25" black
     dot, three per sheet, measured from the sheet's top-left corner.
     24×24 sheet (22×22 + circle): (6.596, 0.378), (17.531, 0.378),
     (12, 23.597). 20×26 sheet (18×24): (5.5, 0.5), (14.5, 0.5),
     (10, 25.5). The circle template's own marks sit ~0.06" off the square
     sheet's; the square values are used for both because the yard-sign
     circle template matches them exactly.                                  */
  var CFG = { variants: {}, templates: [], bgSwatches: [], print: { dpi: 150, bleedIn: 1, cropTicks: true, cornerRadiusIn: 1,
    marks: { enabled: true, squareIn: 0.451, dotIn: 0.25,
      positions: {
        square:    [[6.596, 0.378], [17.531, 0.378], [12.0, 23.597]],
        circle:    [[6.596, 0.378], [17.531, 0.378], [12.0, 23.597]],
        rectangle: [[5.5, 0.5], [14.5, 0.5], [10.0, 25.5]]
      } } } };
  try {
    var cfgEl = document.querySelector("[data-config-json]");
    if (cfgEl) {
      var parsed = JSON.parse(cfgEl.textContent || "{}");
      if (parsed.variants) CFG.variants = parsed.variants;
      if (Array.isArray(parsed.templates)) {
        CFG.templates = parsed.templates.filter(function (t) { return t && t.id && t.img; });
      }
      if (typeof parsed.bgSwatches === "string" && parsed.bgSwatches.trim()) {
        parsed.bgSwatches.split(",").forEach(function (tok) {
          var m = tok.trim().match(/^(#[0-9a-fA-F]{3,8})(?:\s+(.+))?$/);
          if (m) CFG.bgSwatches.push({ css: m[1], name: m[2] || m[1] });
        });
      }
      if (parsed.print) {
        CFG.print.dpi = parseFloat(parsed.print.dpi) || CFG.print.dpi;
        CFG.print.cornerRadiusIn = (parsed.print.cornerRadiusIn === 0 || parsed.print.cornerRadiusIn)
          ? parseFloat(parsed.print.cornerRadiusIn) : CFG.print.cornerRadiusIn;
        CFG.print.bleedIn = (parsed.print.bleedIn === 0 || parsed.print.bleedIn) ? parseFloat(parsed.print.bleedIn) : CFG.print.bleedIn;
        CFG.print.cropTicks = parsed.print.cropTicks !== false;
        if (parsed.print.marks) {
          var m = parsed.print.marks;
          CFG.print.marks.enabled = m.enabled !== false;
          CFG.print.marks.squareIn = parseFloat(m.squareIn) || CFG.print.marks.squareIn;
          CFG.print.marks.dotIn = parseFloat(m.dotIn) || CFG.print.marks.dotIn;
          var pos = m.positions;
          if (typeof pos === "string" && pos.trim()) {
            try { pos = JSON.parse(pos); } catch (pe) { pos = null; }
          }
          if (pos && typeof pos === "object") {
            Object.keys(pos).forEach(function (k) {
              if (Array.isArray(pos[k]) && pos[k].length) {
                CFG.print.marks.positions[k] = pos[k];
              }
            });
          }
        }
      }
    }
  } catch (e) { /* keep defaults */ }

  /* ── Template registry ─────────────────────────────────────────────
     ASSET mode: templates come from theme-editor blocks, each with a
     full-sheet transparent design PNG (white artwork only).
     Fallback: the built-in canvas-drawn templates in sign-templates.js. */
  var ASSET_MODE = CFG.templates.length > 0;
  var REG = {};
  if (ASSET_MODE) {
    CFG.templates.forEach(function (t) { REG[String(t.id)] = t; });
  } else {
    Object.keys(ST.TEMPLATES).forEach(function (k) {
      REG[k] = { id: k, name: ST.TEMPLATES[k].name, drawn: true };
    });
  }

  var imgCache = {};
  function overlayImage(url, onReady) {
    if (!url) return null;
    var entry = imgCache[url];
    if (!entry) {
      var im = new Image();
      im.crossOrigin = "anonymous";
      entry = imgCache[url] = { img: im, ready: false, callbacks: [] };
      im.onload = function () {
        entry.ready = true;
        entry.callbacks.splice(0).forEach(function (cb) { cb(); });
      };
      im.onerror = function () { console.warn("Design image failed to load:", url); };
      im.src = url;
    }
    if (onReady && !entry.ready) entry.callbacks.push(onReady);
    return entry;
  }

  /* Which overlay file serves the current shape, and whether its sheet
     matches (drawn 1:1) or the art must be cropped/fit out of it.      */
  function overlayFor(tpl, shapeKey) {
    if (!tpl || tpl.drawn) return null;
    if (shapeKey === "rectangle") {
      if (tpl.imgRect) return { url: tpl.imgRect, sheetMatches: true };
      return { url: tpl.img, sheetMatches: false };
    }
    if (shapeKey === "circle" && tpl.imgCircle) {
      return { url: tpl.imgCircle, sheetMatches: true };
    }
    return { url: tpl.img, sheetMatches: true }; /* square sheet, 24×24 */
  }

  var SIDES = {
    single: { label: "Single Sided", delta: 0 },
    double: { label: "Double Sided", delta: 10 }
  };
  var METHODS = { template: "Start From a Template", upload: "Upload Your Artwork" };
  var BASE_PRICE = 30;
  var MIN_PPI_OK = 75;    /* below this an upload is blocked */
  var MIN_PPI_GOOD = 150; /* at/above this it's flagged good  */

  /* ── STATE ────────────────────────────────────────────────────────── */
  var state = {
    sides: "double",
    shape: "square",
    method: null,            /* 'template' | 'upload' */
    templateId: null,
    backTemplateId: null,    /* double-sided: back side's design art */
    backSame: true,          /* back mirrors front (art + colors) */
    previewSide: "front",
    design: { bg: null, textColor: "auto", ink: "white", fields: {} },
    designBack: null,        /* {bg, textColor, ink} when back is customized */
    logoImg: null,
    logoFile: null,          /* original logo file, kept so cart edits can restore it */
    logoScale: 1,
    activeImage: 0,
    quantity: 1,
    customMode: false,
    customValue: "",
    uploads: { 0: null, 1: null }, /* {file, img, ppi} per side */
    approved: false,
    tplApproved: false,
    generating: false,
    galleryOverride: false, /* user picked a product photo over the design photo */
    editKey: null           /* cart line key being edited (?edit=…&key=…) */
  };

  /* ── DERIVED ──────────────────────────────────────────────────────── */
  function variantKey() { return state.sides + "|" + state.shape; }
  function matchedVariant() { return CFG.variants[variantKey()] || null; }
  function price() {
    var mv = matchedVariant();
    if (mv && typeof mv.price === "number") return mv.price / 100;
    return BASE_PRICE + (SIDES[state.sides] ? SIDES[state.sides].delta : 0);
  }
  function qty() {
    if (state.customMode) {
      var n = parseInt(state.customValue || "0", 10);
      return Math.max(1, n || 1);
    }
    return state.quantity;
  }
  function shapeSpec() { return ST.SHAPES[state.shape] || ST.SHAPES.square; }
  function configStr() {
    return (SIDES[state.sides] ? SIDES[state.sides].label : "") + " · " + shapeSpec().label;
  }
  function isDouble() { return state.sides === "double"; }
  function uploadOk(u) { return !!(u && u.img && u.ppi >= MIN_PPI_OK); }
  function uploadsComplete() {
    return uploadOk(state.uploads[0]) && (!isDouble() || uploadOk(state.uploads[1]));
  }
  function currentTpl() { return REG[String(state.templateId)] || null; }
  function backCustomized() { return isDouble() && !state.backSame && !!state.designBack; }
  function backActive() { return backCustomized() && !!REG[String(state.backTemplateId)]; }
  /* the design object a given printed side renders with (hashtag/logo shared) */
  function sideDesign(side) {
    if (side === "back" && backCustomized()) {
      return { bg: state.designBack.bg, textColor: state.designBack.textColor,
               ink: state.designBack.ink, fields: state.design.fields };
    }
    return state.design;
  }
  /* the design object the editor controls currently edit */
  function activeDesign() {
    return (backCustomized() && state.previewSide === "back") ? state.designBack : state.design;
  }
  function activeTpl() {
    return tplForSide(backCustomized() && state.previewSide === "back" ? "back" : "front");
  }
  function tplForSide(side) {
    if (side === "back" && backCustomized() && REG[String(state.backTemplateId)]) {
      return REG[String(state.backTemplateId)];
    }
    return currentTpl();
  }
  function tplReadyToDraw(tpl) {
    if (!tpl) return false;
    if (tpl.drawn) return true;
    var ov = overlayFor(tpl, state.shape);
    var entry = ov && overlayImage(ov.url);
    return !!(entry && entry.ready);
  }
  function ready() {
    if (state.generating) return false;
    if (state.method === "template") {
      var tpl = currentTpl();
      if (!tpl) return false;
      if (isDouble() && !state.backSame && !backActive()) return false;
      if (!state.tplApproved) return false;
      if (!tplReadyToDraw(tpl)) return false;
      if (backActive() && !tplReadyToDraw(tplForSide("back"))) return false;
      return true;
    }
    if (state.method === "upload") return uploadsComplete() && state.approved;
    return false;
  }

  /* ── ELEMENTS ─────────────────────────────────────────────────────── */
  var stepEls = {};
  root.querySelectorAll("[data-step]").forEach(function (el) {
    stepEls[el.getAttribute("data-step")] = el;
  });
  var featuredSign = root.querySelector("[data-featured-sign]");
  var featuredImg = root.querySelector("[data-featured-img]");
  var dots = root.querySelectorAll("[data-dot]");
  var thumbs = root.querySelectorAll("[data-thumb]");
  var tplNotice = root.querySelector("[data-tpl-notice]");
  var configEl = root.querySelector("[data-config]");
  var unitEl = root.querySelector("[data-unit]");
  var totalEl = root.querySelector("[data-total]");
  var sidesDeltaEl = root.querySelector("[data-sides-delta]");
  var addBtn = root.querySelector("[data-addbtn]");
  var addLabel = root.querySelector("[data-addlabel]");
  var cartForm = root.querySelector("[data-cart-form]");
  var qtyInput = root.querySelector("[data-qty-input]");
  var variantIdInput = root.querySelector("[data-variant-id]");
  var printFileFront = root.querySelector("[data-print-file-front]");
  var printFileBack = root.querySelector("[data-print-file-back]");
  var previewFileFront = root.querySelector("[data-preview-file-front]");
  var previewFileBack = root.querySelector("[data-preview-file-back]");
  var editorCanvas = root.querySelector("[data-editor-canvas]");
  var editorFields = root.querySelector("[data-editor-fields]");
  var bgSwatches = root.querySelector("[data-bg-swatches]");
  var txtSwatches = root.querySelector("[data-txt-swatches]");
  var approveRow = root.querySelector("[data-approve-row]");
  var approveBox = root.querySelector("[data-approve]");
  var tplApproveRow = root.querySelector("[data-tpl-approve-row]");
  var tplApproveBox = root.querySelector("[data-tpl-approve]");
  var backSection = root.querySelector("[data-back-section]");
  var backSameBox = root.querySelector("[data-back-same]");
  var backGrid = root.querySelector("[data-back-grid]");
  var backNotice = root.querySelector("[data-back-notice]");
  var frontSelectedBar = root.querySelector("[data-front-selected]");
  var frontSelectedName = root.querySelector("[data-front-selected-name]");
  var backSelectedBar = root.querySelector("[data-back-selected]");
  var backSelectedName = root.querySelector("[data-back-selected-name]");
  var sideTabs = root.querySelector("[data-side-tabs]");
  var props = {};
  root.querySelectorAll("[data-prop]").forEach(function (el) {
    props[el.getAttribute("data-prop")] = el;
  });
  var GALLERY = ["rgb(234,0,138)", "rgb(255,240,0)", "rgb(0,172,237)", "rgb(35,31,32)"];

  function money(n) { return "$" + n.toFixed(2); }

  /* ── STEP COMPLETION / NUMBERING ──────────────────────────────────── */
  function setComplete(stepKey, complete) {
    var el = stepEls[stepKey];
    if (!el) return;
    var numEl = el.querySelector("[data-stepnum]");
    if (numEl) numEl.classList.toggle("pdp-stephead__num--complete", !!complete);
  }
  function setNumber(stepKey, n) {
    var el = stepEls[stepKey];
    if (!el) return;
    var nEl = el.querySelector(".pdp-stephead__n");
    if (nEl) nEl.textContent = String(n);
  }

  /* ── CANVAS PREVIEWS ──────────────────────────────────────────────── */
  var previewQueued = false;
  function queuePreview() {
    if (previewQueued) return;
    previewQueued = true;
    requestAnimationFrame(function () {
      previewQueued = false;
      drawEditorPreview();
    });
  }

  /* Draw the full print sheet (used by both the preview crop and the
     print-file render). No bleed furniture in the preview — the preview
     blits only the trim area with rounded corners.                       */
  function drawFullSheet(ctx, ppi, forPreview, side) {
    var tpl = tplForSide(side || "front");
    if (!tpl) return;
    var design = sideDesign(side || "front");
    if (tpl.drawn) {
      ST.render({
        ctx: ctx, templateId: tpl.id, design: design,
        shape: state.shape, bleedIn: CFG.print.bleedIn, ppi: ppi,
        logoImg: state.logoImg, logoScale: state.logoScale, guides: !!forPreview
      });
      return;
    }
    var ov = overlayFor(tpl, state.shape);
    var entry = ov && overlayImage(ov.url, queuePreview);
    ST.renderOverlay({
      ctx: ctx,
      img: entry && entry.ready ? entry.img : null,
      design: design,
      shape: state.shape,
      bleedIn: CFG.print.bleedIn,
      ppi: ppi,
      logoImg: state.logoImg,
      logoScale: state.logoScale,
      sheetMatches: ov ? ov.sheetMatches : true,
      defaultBg: tpl.bg,
      guides: !!forPreview
    });
  }

  function drawEditorPreview() {
    if (!editorCanvas || !currentTpl()) return;
    var holder = editorCanvas.parentElement;
    var cssW = Math.max(240, Math.min(560, holder.clientWidth || 480));
    ST.renderPreviewInto(editorCanvas, cssW, {
      shape: state.shape,
      bleedIn: CFG.print.bleedIn,
      cornerRadiusIn: CFG.print.cornerRadiusIn,
      dpr: Math.min(2, window.devicePixelRatio || 1),
      drawFull: function (ctx, ppi) { drawFullSheet(ctx, ppi, true, state.previewSide); }
    });
  }

  /* Draw one design thumbnail onto a tile canvas (front + back grids). */
  function drawTileCanvas(cv, tpl) {
    if (!cv || !tpl) return;
    var draw = function () {
      /* render at the tile frame's real size (and pixel density) so the
         thumbnail is neither clipped nor pixelated */
      var frame = cv.parentElement;
      var cssW = 220;
      if (frame && frame.clientWidth) {
        var pad = 16; /* .pdp-tpl__frame horizontal padding */
        cssW = Math.max(100, Math.min(400, frame.clientWidth - pad));
      }
      ST.renderPreviewInto(cv, cssW, {
        shape: "square",
        bleedIn: CFG.print.bleedIn,
        cornerRadiusIn: CFG.print.cornerRadiusIn,
        dpr: Math.min(2, window.devicePixelRatio || 1),
        drawFull: function (ctx, ppi) {
          if (tpl.drawn) {
            ST.render({ ctx: ctx, templateId: tpl.id, design: { fields: {} },
                        shape: "square", bleedIn: CFG.print.bleedIn, ppi: ppi });
          } else {
            var entry = overlayImage(tpl.thumb || tpl.img);
            ST.renderOverlay({ ctx: ctx, img: entry && entry.ready ? entry.img : null,
              design: { bg: null, ink: tpl.ink || "white", fields: {} },
              shape: "square", bleedIn: CFG.print.bleedIn, ppi: ppi,
              sheetMatches: true,
              defaultBg: tpl.bg });
          }
        }
      });
      cv.style.width = "100%";
      cv.style.height = "auto";
    };
    if (tpl.drawn) {
      draw();
    } else {
      var entry = overlayImage(tpl.thumb || tpl.img, draw);
      if (entry && entry.ready) draw();
    }
  }

  /* Build the front design grid from the registry (empty in Liquid). */
  function buildFrontGrid() {
    var grid = root.querySelector("[data-tpl-grid]");
    if (!grid || grid.childElementCount) return;
    Object.keys(REG).forEach(function (id) {
      var tpl = REG[id];
      var tile = document.createElement("div");
      tile.className = "pdp-tpl";
      tile.setAttribute("data-template", id);
      tile.setAttribute("data-tag", tpl.tag || "");
      tile.innerHTML =
        '<div class="pdp-tpl__frame"><canvas class="pdp-tpl__cv" data-tpl-canvas="' + id + '"></canvas></div>' +
        '<div class="pdp-tpl__body"><span class="pdp-tpl__name"></span></div>' +
        '<div class="pdp-tpl__overlay"><span class="pdp-tpl__cta">CUSTOMIZE \u2192</span></div>' +
        '<span class="pdp-tpl__stamp">SELECTED</span>';
      tile.querySelector(".pdp-tpl__name").textContent = tpl.name || "Design";
      tile.addEventListener("click", function () { selectTemplate(id); });
      grid.appendChild(tile);
    });
  }
  function drawTemplateTiles() {
    root.querySelectorAll("[data-tpl-canvas]").forEach(function (cv) {
      drawTileCanvas(cv, REG[String(cv.getAttribute("data-tpl-canvas"))]);
    });
  }

  /* Any design edit voids a previous "approved for print" tick. */
  function touchDesign() {
    if (state.tplApproved) {
      state.tplApproved = false;
      if (tplApproveBox) tplApproveBox.checked = false;
      render();
    }
  }

  /* ── EDITOR UI (fields + swatches + logo) ─────────────────────────── */
  function buildEditor() {
    var tpl = currentTpl();
    if (!tpl || !editorFields) return;

    /* editable fields — template art/text is locked; the #hashtag is the
       only text customers set */
    editorFields.innerHTML = "";
    addField("hashtag", "#Hashtag (optional)");

    /* background swatches (Theme settings -> Configurator) + custom color */
    if (bgSwatches) {
      bgSwatches.innerHTML = "";
      var swatchList = CFG.bgSwatches.length
        ? CFG.bgSwatches
        : ST.BG_OPTIONS.map(function (k) { return { css: ST.PALETTE[k].css, name: ST.PALETTE[k].name, key: k }; });
      swatchList.forEach(function (sw) {
        var btn = document.createElement("button");
        btn.type = "button";
        btn.className = "pdp-swatch";
        btn.style.background = sw.css;
        btn.title = sw.name;
        btn.setAttribute("data-swatch-bg", sw.key || sw.css);
        btn.addEventListener("click", function () {
          activeDesign().bg = sw.key || sw.css;
          touchDesign();
          syncSwatches();
          queuePreview();
          syncFormProps();
        });
        bgSwatches.appendChild(btn);
      });
      addCustomSwatch(bgSwatches, "bg");
    }

    /* design color: White/Black toggle for PNG designs; the fallback drawn
       templates keep their full text-color swatches */
    if (txtSwatches) {
      txtSwatches.innerHTML = "";
      if (!tpl.drawn) {
        [{ key: "white", css: "#FFFFFF", label: "White design" },
         { key: "black", css: "rgb(35,31,32)", label: "Black design" }].forEach(function (o) {
          var btn = document.createElement("button");
          btn.type = "button";
          btn.className = "pdp-swatch pdp-swatch--ink";
          btn.style.background = o.css;
          btn.title = o.label;
          btn.setAttribute("data-swatch-ink", o.key);
          btn.addEventListener("click", function () {
            activeDesign().ink = o.key;
            touchDesign();
            syncSwatches();
            queuePreview();
            syncFormProps();
          });
          txtSwatches.appendChild(btn);
        });
      } else {
        ST.TEXT_OPTIONS.forEach(function (key) {
          var btn = document.createElement("button");
          btn.type = "button";
          btn.className = "pdp-swatch" + (key === "auto" ? " pdp-swatch--auto" : "");
          if (key !== "auto") btn.style.background = ST.PALETTE[key].css;
          btn.title = key === "auto" ? "Automatic (best contrast)" : ST.PALETTE[key].name;
          btn.setAttribute("data-swatch-txt", key);
          btn.addEventListener("click", function () {
            activeDesign().textColor = key;
            touchDesign();
            syncSwatches();
            queuePreview();
            syncFormProps();
          });
          txtSwatches.appendChild(btn);
        });
        addCustomSwatch(txtSwatches, "txt");
      }
    }
    syncSwatches();
  }

  function addField(id, label) {
    var wrap = document.createElement("div");
    wrap.className = "pdp-field";
    var lbl = document.createElement("label");
    lbl.className = "pdp-field__label";
    lbl.textContent = label.toUpperCase();
    var input = document.createElement("input");
    input.type = "text";
    input.className = "pdp-field__input";
    input.value = (typeof state.design.fields[id] === "string") ? state.design.fields[id] : "";
    input.placeholder = "#YOURCHURCH";
    input.setAttribute("maxlength", "40");
    input.addEventListener("input", function () {
      state.design.fields[id] = input.value;
      touchDesign();
      queuePreview();
      syncFormProps();
    });
    wrap.appendChild(lbl);
    wrap.appendChild(input);
    editorFields.appendChild(wrap);
  }

  /* "Any color" swatch — a native color picker for exact brand colors. */
  function addCustomSwatch(row, kind) {
    var wrap = document.createElement("label");
    wrap.className = "pdp-swatch pdp-swatch--custom";
    wrap.title = "Custom color \u2014 pick any color";
    wrap.setAttribute("data-swatch-custom", kind);
    var input = document.createElement("input");
    input.type = "color";
    input.value = "#5E1C2C";
    input.className = "pdp-swatch__picker";
    input.addEventListener("input", function () {
      if (kind === "bg") activeDesign().bg = input.value;
      else activeDesign().textColor = input.value;
      touchDesign();
      syncSwatches();
      queuePreview();
      syncFormProps();
    });
    wrap.appendChild(input);
    row.appendChild(wrap);
  }

  function isHex(v) { return typeof v === "string" && v.charAt(0) === "#"; }

  function normColor(v) {
    return (typeof v === "string") ? v.toLowerCase().replace(/\s+/g, "") : "";
  }
  function syncSwatches() {
    var tpl = activeTpl();
    var d = activeDesign();
    var bgVal = d.bg || (tpl ? tpl.bg : null);
    var txtVal = d.textColor || "auto";
    var bgMatched = false;
    root.querySelectorAll("[data-swatch-ink]").forEach(function (b) {
      b.classList.toggle("is-selected", b.getAttribute("data-swatch-ink") === (d.ink || "white"));
    });
    root.querySelectorAll("[data-swatch-bg]").forEach(function (b) {
      var hit = normColor(b.getAttribute("data-swatch-bg")) === normColor(bgVal);
      if (hit) bgMatched = true;
      b.classList.toggle("is-selected", hit);
    });
    root.querySelectorAll("[data-swatch-txt]").forEach(function (b) {
      b.classList.toggle("is-selected", b.getAttribute("data-swatch-txt") === txtVal);
    });
    root.querySelectorAll("[data-swatch-custom]").forEach(function (w) {
      var kind = w.getAttribute("data-swatch-custom");
      var val = kind === "bg" ? bgVal : txtVal;
      var active = isHex(val) && (kind === "bg" ? !bgMatched : true);
      w.classList.toggle("is-selected", active);
      w.style.background = active ? val : "";
    });
  }

  /* logo upload */
  var logoInput = root.querySelector("[data-logo-input]");
  var logoBtn = root.querySelector("[data-logo-btn]");
  var logoRemove = root.querySelector("[data-logo-remove]");
  var logoName = root.querySelector("[data-logo-name]");
  if (logoBtn && logoInput) {
    logoBtn.addEventListener("click", function () { logoInput.click(); });
    logoInput.addEventListener("change", function () {
      var f = logoInput.files && logoInput.files[0];
      if (!f) return;
      if (!/^image\/(png|jpeg)$/.test(f.type)) {
        alert("Logo must be a PNG or JPG image.");
        logoInput.value = "";
        return;
      }
      var img = new Image();
      img.onload = function () {
        state.logoImg = img;
        state.logoFile = f;
        touchDesign();
        if (logoName) logoName.textContent = f.name;
        if (logoRemove) logoRemove.hidden = false;
        syncLogoScaleUI();
        queuePreview();
      };
      img.src = URL.createObjectURL(f);
    });
  }
  if (logoRemove) {
    logoRemove.addEventListener("click", function () {
      state.logoImg = null;
      state.logoFile = null;
      state.logoScale = 1;
      touchDesign();
      if (logoInput) logoInput.value = "";
      if (logoName) logoName.textContent = "";
      logoRemove.hidden = true;
      syncLogoScaleUI();
      queuePreview();
    });
  }

  /* logo size slider — shown once a logo is uploaded */
  var logoScaleRow = root.querySelector("[data-logo-scale-row]");
  var logoScaleInput = root.querySelector("[data-logo-scale]");
  var logoScaleVal = root.querySelector("[data-logo-scale-val]");
  function syncLogoScaleUI() {
    if (logoScaleRow) logoScaleRow.hidden = !state.logoImg;
    if (logoScaleInput) logoScaleInput.value = String(Math.round(state.logoScale * 100));
    if (logoScaleVal) logoScaleVal.textContent = Math.round(state.logoScale * 100) + "%";
  }
  if (logoScaleInput) {
    logoScaleInput.addEventListener("input", function () {
      state.logoScale = (parseInt(logoScaleInput.value, 10) || 100) / 100;
      touchDesign();
      if (logoScaleVal) logoScaleVal.textContent = Math.round(state.logoScale * 100) + "%";
      queuePreview();
      syncFormProps();
    });
  }

  /* ── RENDER (UI state) ────────────────────────────────────────────── */
  function render() {
    root.querySelectorAll("[data-option]").forEach(function (btn) {
      var opt = btn.getAttribute("data-option");
      var val = btn.getAttribute("data-value");
      btn.classList.toggle("is-selected", state[opt] === val);
    });

    setComplete("sides", !!state.sides);
    setComplete("shape", !!state.shape);
    setComplete("method", !!state.method);

    var showUpload = state.method === "upload";
    var showTemplates = state.method === "template";
    var showEditor = showTemplates && !!currentTpl();
    var showQuantity = showUpload || showEditor;

    stepEls.upload.hidden = !showUpload;
    stepEls.templates.hidden = !showTemplates;
    stepEls.editor.hidden = !showEditor;
    stepEls.quantity.hidden = !showQuantity;

    /* Flow: 1 Sides → 2 Shape → 3 Method → 4 Design/Upload → 5 Customize → 6 Quantity
       (upload path has no editor step, so its Quantity is step 5). */
    if (showUpload) {
      setNumber("upload", 4);
      setNumber("quantity", 5);
      setComplete("upload", uploadsComplete() && state.approved);
    } else if (showTemplates) {
      setNumber("templates", 4);
      setNumber("editor", 5);
      setNumber("quantity", 6);
      setComplete("templates", !!currentTpl());
      setComplete("editor", showEditor && state.tplApproved);
    }
    setComplete("quantity", true);

    if (tplNotice) tplNotice.hidden = !!currentTpl();
    root.querySelectorAll("[data-template]").forEach(function (tpl) {
      var id = String(tpl.getAttribute("data-template"));
      tpl.classList.toggle("is-selected", String(state.templateId) === id);
    });

    /* gallery — the selected design's lifestyle photo (if it has one) takes
       over the featured image; otherwise the product's own photos show */
    var selTpl = (state.method === "template") ? currentTpl() : null;
    /* per-shape lifestyle photo when available, else the design's main photo */
    var designPhoto = null;
    if (selTpl) {
      if (state.shape === "rectangle" && selTpl.photoRect) designPhoto = selTpl.photoRect;
      else if (state.shape === "circle" && selTpl.photoCircle) designPhoto = selTpl.photoCircle;
      else if (selTpl.photo) designPhoto = selTpl.photo;
    }
    if (featuredSign) featuredSign.style.background = GALLERY[state.activeImage % GALLERY.length];
    if (featuredImg) {
      if (designPhoto && !state.galleryOverride) {
        if (featuredImg.getAttribute("src") !== designPhoto) {
          featuredImg.src = designPhoto;
          featuredImg.alt = (selTpl.name || "Sign") + " greeting sign";
        }
      } else {
        var activeThumb = thumbs[state.activeImage];
        if (activeThumb && activeThumb.getAttribute("data-img")) {
          featuredImg.src = activeThumb.getAttribute("data-img");
        }
      }
    }
    var showingDesign = !!(designPhoto && !state.galleryOverride);
    /* no-product-photo fallback: toggle placeholder tile vs. design photo */
    var featuredTile = root.querySelector("[data-featured-tile]");
    if (featuredTile && featuredImg) {
      featuredTile.hidden = showingDesign;
      featuredImg.hidden = !showingDesign;
    }
    dots.forEach(function (d, i) { d.classList.toggle("is-active", !showingDesign && i === state.activeImage); });
    thumbs.forEach(function (t, i) { t.classList.toggle("is-active", !showingDesign && i === state.activeImage); });

    /* quantity */
    root.querySelectorAll("[data-qty]").forEach(function (tile) {
      var n = parseInt(tile.getAttribute("data-qty"), 10);
      tile.classList.toggle("is-active", !state.customMode && state.quantity === n);
    });
    var customTile = root.querySelector("[data-qty-custom]");
    if (customTile) customTile.classList.toggle("is-active", state.customMode);
    var customRow = root.querySelector("[data-qty-customrow]");
    if (customRow) customRow.hidden = !state.customMode;

    /* approval rows */
    if (approveRow) approveRow.hidden = !(showUpload && uploadsComplete());
    if (tplApproveRow) tplApproveRow.hidden = !showEditor;

    /* back-side design section + preview tabs (double-sided template path) */
    var frontTpl = currentTpl();
    if (frontSelectedBar) {
      frontSelectedBar.hidden = !frontTpl;
      if (frontTpl && frontSelectedName) frontSelectedName.textContent = frontTpl.name || "";
    }
    if (backSection) backSection.hidden = !(showTemplates && isDouble());
    if (backSameBox) backSameBox.checked = state.backSame;
    if (backGrid) {
      backGrid.hidden = state.backSame;
      root.querySelectorAll("[data-back-tpl]").forEach(function (tile) {
        tile.classList.toggle("is-selected",
          String(state.backTemplateId) === String(tile.getAttribute("data-back-tpl")));
      });
    }
    if (backSelectedBar) {
      backSelectedBar.hidden = !(isDouble() && backActive());
      if (backActive() && backSelectedName) backSelectedName.textContent = tplForSide("back").name || "";
    }
    if (backNotice) backNotice.hidden = !(showTemplates && isDouble() && !state.backSame && !backActive());
    if (sideTabs) {
      sideTabs.hidden = !(showEditor && backCustomized());
      sideTabs.querySelectorAll("[data-side-tab]").forEach(function (tab) {
        tab.classList.toggle("is-active", tab.getAttribute("data-side-tab") === state.previewSide);
      });
      var note = sideTabs.querySelector("[data-editing-note]");
      if (note) {
        note.textContent = state.previewSide === "back"
          ? "Editing the back — colors apply to this side; logo & #hashtag print on both."
          : "Editing the front.";
      }
    }

    /* pricing / sticky bar */
    var p = price(), q = qty();
    if (configEl) configEl.textContent = configStr();
    if (unitEl) unitEl.textContent = money(p) + " × " + q;
    if (totalEl) totalEl.textContent = money(p * q);
    /* double-sided upcharge for the current shape, from the real variant prices */
    if (sidesDeltaEl) {
      var vs = CFG.variants["single|" + state.shape], vd = CFG.variants["double|" + state.shape];
      var delta = vs && vd ? (vd.price - vs.price) / 100 : 0;
      sidesDeltaEl.hidden = !(delta > 0);
      if (delta > 0) sidesDeltaEl.textContent = "+" + money(delta).replace(/\.00$/, "");
    }

    var isReady = ready();
    if (addBtn) {
      addBtn.classList.toggle("is-ready", isReady);
      addBtn.disabled = !isReady;
    }
    if (addLabel) {
      addLabel.textContent = state.generating ? "PREPARING PRINT FILE…"
        : (isReady ? (state.editKey ? "UPDATE CART" : "ADD TO CART") : "FINISH CHOOSING");
    }

    syncFormProps();
  }

  function syncFormProps() {
    var mv = matchedVariant();
    if (variantIdInput && mv && mv.id) variantIdInput.value = String(mv.id);
    if (qtyInput) qtyInput.value = String(qty());
    if (props["Sides"]) props["Sides"].value = SIDES[state.sides] ? SIDES[state.sides].label : "";
    if (props["Shape"]) props["Shape"].value = shapeSpec().label;
    if (props["Design method"]) props["Design method"].value = state.method ? METHODS[state.method] : "";
    if (props["Template"]) {
      var t = currentTpl();
      var label = (state.method === "template" && t) ? t.name : "";
      if (label && backCustomized()) label += " / Back: " + tplForSide("back").name;
      props["Template"].value = label;
    }
    if (props["design"]) props["design"].value = designJson();
  }

  function designJson() {
    var tpl = currentTpl();
    if (state.method === "template" && tpl) {
      var ov = overlayFor(tpl, state.shape);
      return JSON.stringify({
        v: 2, method: "template",
        template: String(state.templateId), name: tpl.name,
        back: backCustomized() ? {
          template: String(state.backTemplateId),
          name: tplForSide("back").name,
          bg: state.designBack.bg || tplForSide("back").bg,
          ink: state.designBack.ink || "white"
        } : undefined,
        designImg: ov ? ov.url : null,
        shape: state.shape, sides: state.sides,
        bg: state.design.bg || tpl.bg,
        ink: tpl.drawn ? undefined : (state.design.ink || "white"),
        textColor: tpl.drawn ? (state.design.textColor || "auto") : undefined,
        fields: state.design.fields,
        logo: !!state.logoImg,
        logoScale: state.logoImg ? state.logoScale : undefined,
        bleedIn: CFG.print.bleedIn, dpi: CFG.print.dpi
      });
    }
    if (state.method === "upload") {
      var files = [];
      [0, 1].forEach(function (i) {
        var u = state.uploads[i];
        if (u && u.file) files.push({ side: i + 1, name: u.file.name, ppi: Math.round(u.ppi) });
      });
      return JSON.stringify({
        v: 1, method: "upload", shape: state.shape, sides: state.sides,
        files: files, approved: state.approved,
        bleedIn: CFG.print.bleedIn, dpi: CFG.print.dpi
      });
    }
    return "";
  }

  /* ── EVENTS — option cards ────────────────────────────────────────── */
  root.querySelectorAll("[data-option]").forEach(function (btn) {
    btn.addEventListener("click", function () {
      var opt = btn.getAttribute("data-option");
      var val = btn.getAttribute("data-value");
      state[opt] = val;
      if (opt === "method" && val === "upload") state.templateId = null;
      if (opt === "shape") {
        /* shape changes physical size → re-preflight uploads */
        [0, 1].forEach(function (i) { repreflight(i); });
        touchDesign();
        queuePreview();
      }
      if (opt === "sides") {
        state.approved = false;
        if (approveBox) approveBox.checked = false;
        if (val === "single") state.previewSide = "front";
        touchDesign();
        queuePreview();
      }
      render();
    });
  });

  /* ── EVENTS — gallery thumbs ──────────────────────────────────────── */
  thumbs.forEach(function (t, i) {
    t.addEventListener("click", function () {
      state.activeImage = i;
      state.galleryOverride = true; /* explicit choice beats the design photo */
      render();
    });
  });

  /* ── EVENTS — template gallery ────────────────────────────────────── */
  root.querySelectorAll("[data-filter]").forEach(function (chip) {
    chip.addEventListener("click", function () {
      var filter = chip.getAttribute("data-filter");
      root.querySelectorAll("[data-filter]").forEach(function (c) {
        c.classList.toggle("is-active", c === chip);
      });
      root.querySelectorAll("[data-template]").forEach(function (tpl) {
        var tag = tpl.getAttribute("data-tag");
        tpl.hidden = !(filter === "All" || tag === filter);
      });
    });
  });
  function selectTemplate(newId) {
    newId = String(newId);
    if (!REG[newId]) return;
    if (newId !== String(state.templateId)) {
      state.templateId = newId;
      state.galleryOverride = false; /* new design → show its photo again */
      /* fresh template: keep the hashtag + logo, reset colors to defaults */
      var hashtag = state.design.fields.hashtag;
      var t = currentTpl();
      state.design = { bg: null, textColor: "auto",
                       ink: (t && t.ink) || "white", fields: {} };
      state.tplApproved = false;
      if (tplApproveBox) tplApproveBox.checked = false;
      if (typeof hashtag === "string") state.design.fields.hashtag = hashtag;
      buildEditor();
      queuePreview();
    }
    render();
  }
  /* legacy: tiles rendered by Liquid (fallback drawn templates) */
  root.querySelectorAll("[data-tpl-grid] [data-template]").forEach(function (tpl) {
    tpl.addEventListener("click", function () { selectTemplate(tpl.getAttribute("data-template")); });
  });

  /* ── EVENTS — quantity ────────────────────────────────────────────── */
  root.querySelectorAll("[data-qty]").forEach(function (tile) {
    tile.addEventListener("click", function () {
      state.customMode = false;
      state.quantity = parseInt(tile.getAttribute("data-qty"), 10);
      render();
    });
  });
  var customTileEl = root.querySelector("[data-qty-custom]");
  if (customTileEl) {
    customTileEl.addEventListener("click", function () {
      state.customMode = true;
      render();
      var input = root.querySelector("[data-qty-custominput]");
      if (input) input.focus();
    });
  }
  var customInputEl = root.querySelector("[data-qty-custominput]");
  if (customInputEl) {
    customInputEl.addEventListener("input", function () {
      state.customValue = customInputEl.value;
      render();
    });
  }

  /* ── UPLOAD PATH — zones + preflight ──────────────────────────────── */
  function ppiBadge(ppi) {
    if (ppi >= MIN_PPI_GOOD) return { cls: "good", label: "GREAT RESOLUTION" };
    if (ppi >= MIN_PPI_OK) return { cls: "ok", label: "OK — SLIGHTLY SOFT UP CLOSE" };
    return { cls: "low", label: "TOO LOW TO PRINT" };
  }

  function preflight(idx) {
    var u = state.uploads[idx];
    var panel = root.querySelector('[data-uz-preflight="' + idx + '"]');
    if (!panel) return;
    var cv = panel.querySelector("[data-uz-canvas]");
    var statsEl = panel.querySelector("[data-uz-stats]");
    if (!u || !u.img) { panel.hidden = true; return; }

    var spec = shapeSpec();
    var b = CFG.print.bleedIn;
    u.ppi = ST.uploadPpi(u.img.width, u.img.height, state.shape, b);

    /* crop preview at ~440px wide */
    var ppi = 440 / (spec.wIn + 2 * b);
    cv.width = Math.round((spec.wIn + 2 * b) * ppi);
    cv.height = Math.round((spec.hIn + 2 * b) * ppi);
    ST.renderUpload({ ctx: cv.getContext("2d"), img: u.img, shape: state.shape, bleedIn: b, ppi: ppi, guides: true });

    /* crop-loss estimate: how much of the image falls outside the print area */
    var tW = spec.wIn + 2 * b, tH = spec.hIn + 2 * b;
    var imgAspect = u.img.width / u.img.height, tAspect = tW / tH;
    var kept = imgAspect > tAspect ? (tAspect / imgAspect) : (imgAspect / tAspect);
    var lossPct = Math.round((1 - kept) * 100);
    var badge = ppiBadge(u.ppi);

    var html = '<span class="pdp-pf-badge pdp-pf-badge--' + badge.cls + '">' + badge.label + "</span>";
    if (u.isPdf && u.img.pageWIn) {
      /* PDFs are rasterized at print quality; what matters is page size vs sign */
      var pw = Math.round(u.img.pageWIn * 100) / 100, ph = Math.round(u.img.pageHIn * 100) / 100;
      html += '<span class="pdp-pf-line">PDF page ' + pw + '" × ' + ph + '" scaled to fill a ' +
              spec.label.replace("·", "").trim() + " (incl. " + b + '" bleed) — rendered at print resolution</span>';
    } else {
      html += '<span class="pdp-pf-line">' + u.img.width + " × " + u.img.height +
              "px → " + Math.round(u.ppi) + " DPI on a " + spec.label.replace("·", "").trim() + " (incl. " + b + '" bleed)</span>';
    }
    if (lossPct > 2) {
      html += '<span class="pdp-pf-line pdp-pf-line--warn">Your image proportions don’t match the sign — about ' +
              lossPct + "% will be trimmed off the " + (imgAspect > tAspect ? "sides" : "top/bottom") +
              ". Check the dashed cut line in the preview.</span>";
    }
    if (badge.cls === "low") {
      html += '<span class="pdp-pf-line pdp-pf-line--err">This file is below ' + MIN_PPI_OK +
              " DPI at print size and would look blurry. Please upload a larger file (" +
              Math.ceil(tW * MIN_PPI_OK) + " × " + Math.ceil(tH * MIN_PPI_OK) + "px or more).</span>";
    } else {
      html += '<span class="pdp-pf-line">Anything inside the dashed line prints; the outer ' + b + '" is bleed for cutting.</span>';
    }
    statsEl.innerHTML = html;
    panel.hidden = false;
  }

  function repreflight(idx) {
    if (state.uploads[idx] && state.uploads[idx].img) preflight(idx);
  }

  /* ── PDF uploads: rasterize page 1 with pdf.js (loaded on demand) ────
     Browsers can't draw PDFs onto canvas natively. We render the first page
     at PDF_RASTER_PPI against the sign's physical size so vector art comes
     out at full print sharpness, then hand the pixels to the same preflight
     + print pipeline as JPG/PNG. pdf.js only loads when a PDF is chosen.  */
  var PDFJS_SRC = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
  var PDFJS_WORKER = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
  var PDF_RASTER_PPI = 200; /* > print DPI (150) so downscale stays crisp */
  var pdfjsLoading = null;
  function loadPdfJs() {
    if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
    if (pdfjsLoading) return pdfjsLoading;
    pdfjsLoading = new Promise(function (resolve, reject) {
      var sc = document.createElement("script");
      sc.src = PDFJS_SRC;
      sc.onload = function () {
        if (!window.pdfjsLib) { reject(new Error("pdf.js failed to initialize")); return; }
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
        resolve(window.pdfjsLib);
      };
      sc.onerror = function () { reject(new Error("pdf.js failed to load")); };
      document.head.appendChild(sc);
    });
    return pdfjsLoading;
  }
  /* Resolve to an Image-like canvas of page 1 (with .width/.height). */
  /* Scan the raw PDF for page boxes (first occurrence = page 1 in practice). */
  function readPdfBoxes(buf) {
    var out = { bleed: null, trim: null };
    try {
      var bytes = new Uint8Array(buf);
      var head = "";
      var lim = Math.min(bytes.length, 4 * 1024 * 1024);
      for (var i = 0; i < lim; i++) head += String.fromCharCode(bytes[i]);
      var grab = function (name) {
        var m = head.match(new RegExp("/" + name + "\\s*\\[\\s*([-\\d.]+)\\s+([-\\d.]+)\\s+([-\\d.]+)\\s+([-\\d.]+)\\s*\\]"));
        return m ? [parseFloat(m[1]), parseFloat(m[2]), parseFloat(m[3]), parseFloat(m[4])] : null;
      };
      out.bleed = grab("BleedBox");
      out.trim = grab("TrimBox");
    } catch (e) { /* ignore */ }
    return out;
  }
  function rasterizePdf(file) {
    var pdfBoxes = { bleed: null, trim: null };
    return loadPdfJs().then(function (pdfjsLib) {
      return file.arrayBuffer();
    }).then(function (buf) {
      pdfBoxes = readPdfBoxes(buf);
      return window.pdfjsLib.getDocument({ data: buf }).promise;
    }).then(function (doc) {
      return doc.getPage(1);
    }).then(function (page) {
      /* Print-ready exports (Illustrator/InDesign) carry registration marks,
         color bars and slug text OUTSIDE the bleed box. Crop to the bleed box
         (or trim box) when the PDF defines one so those never end up in the
         artwork; plain exported PDFs have no such box → full page. The
         boxes are read straight from the file bytes (pdf.js doesn't expose
         them on the page object). */
      var view = page.view; /* [x0, y0, x1, y1] media box in points */
      var art = pdfBoxes.bleed || pdfBoxes.trim || null;
      var mediaW = view[2] - view[0], mediaH = view[3] - view[1];
      var cropped = false;
      if (art && ((art[2] - art[0]) < mediaW - 1 || (art[3] - art[1]) < mediaH - 1)) {
        cropped = true;
      } else {
        art = view;
      }
      var artW = art[2] - art[0], artH = art[3] - art[1];
      var base = page.getViewport({ scale: 1 }); /* 72 pt/in */
      var pageWIn = artW / 72, pageHIn = artH / 72;
      /* scale so the page covers the sheet at PDF_RASTER_PPI, capped for memory */
      var spec = shapeSpec();
      var sheetW = spec.wIn + 2 * CFG.print.bleedIn, sheetH = spec.hIn + 2 * CFG.print.bleedIn;
      var cover = Math.max(sheetW / pageWIn, sheetH / pageHIn);
      var targetPpi = Math.max(PDF_RASTER_PPI, cover * PDF_RASTER_PPI);
      var scale = targetPpi / 72;
      var maxPx = 6000;
      if (base.width * scale > maxPx || base.height * scale > maxPx) {
        scale = maxPx / Math.max(base.width, base.height);
      }
      /* viewport for the full page; then translate so the art box lands at 0,0 */
      var vp = page.getViewport({ scale: scale });
      var cv = document.createElement("canvas");
      cv.width = Math.round(artW * scale); cv.height = Math.round(artH * scale);
      var ctx = cv.getContext("2d");
      ctx.fillStyle = "#FFFFFF"; ctx.fillRect(0, 0, cv.width, cv.height); /* flatten transparency */
      /* PDF y-axis is bottom-up: offset = (art.x0 - view.x0, view.y1 - art.y1) */
      var offX = (art[0] - view[0]) * scale;
      var offY = (view[3] - art[3]) * scale;
      var vpShifted = page.getViewport({ scale: scale, offsetX: -offX, offsetY: -offY });
      return page.render({ canvasContext: ctx, viewport: vpShifted }).promise.then(function () {
        cv.pageWIn = pageWIn; cv.pageHIn = pageHIn; cv.isPdf = true; cv.croppedToBleed = cropped;
        return cv;
      });
    });
  }

  root.querySelectorAll("[data-uploadzone]").forEach(function (zone) {
    var idx = parseInt(zone.getAttribute("data-uploadzone"), 10);
    var input = zone.querySelector("[data-uz-input]");
    var btn = zone.querySelector("[data-uz-btn]");
    var btnLabel = zone.querySelector("[data-uz-btnlabel]");
    var removeBtn = zone.querySelector("[data-uz-remove]");
    var nameEl = zone.querySelector("[data-uz-name]");
    var previewEl = zone.querySelector("[data-uz-preview]");

    function applyFile(file) {
      state.approved = false;
      if (approveBox) approveBox.checked = false;

      if (!file) {
        state.uploads[idx] = null;
        zone.classList.remove("has-file", "is-image", "is-doc");
        if (nameEl) nameEl.textContent = "";
        if (btnLabel) btnLabel.textContent = "CLICK TO UPLOAD";
        if (previewEl) previewEl.removeAttribute("src");
        var panel = root.querySelector('[data-uz-preflight="' + idx + '"]');
        if (panel) panel.hidden = true;
        render();
        return;
      }
      var isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name || "");
      if (!isPdf && !/^image\/(png|jpeg)$/.test(file.type)) {
        alert("Please upload a PDF, JPG or PNG. For the sharpest result, export at the sign's full size.");
        if (input) input.value = "";
        return;
      }
      function accept(img, previewSrc) {
        state.uploads[idx] = { file: file, img: img, ppi: 0, isPdf: !!isPdf };
        zone.classList.add("has-file", "is-image");
        if (nameEl) nameEl.textContent = file.name;
        if (btnLabel) btnLabel.textContent = "REPLACE";
        if (previewEl && previewSrc) previewEl.src = previewSrc;
        preflight(idx);
        render();
      }
      if (isPdf) {
        if (nameEl) nameEl.textContent = "Reading PDF\u2026";
        zone.classList.add("has-file", "is-doc");
        rasterizePdf(file).then(function (cv) {
          zone.classList.remove("is-doc");
          accept(cv, cv.toDataURL("image/jpeg", 0.8));
        }).catch(function (err) {
          console.warn("PDF read failed:", err);
          zone.classList.remove("has-file", "is-doc");
          if (nameEl) nameEl.textContent = "";
          alert("We couldn't read that PDF. Please try re-exporting it (PDF 1.4+, no password), or upload a JPG/PNG.");
          if (input) input.value = "";
        });
        return;
      }
      var img = new Image();
      img.onload = function () { accept(img, img.src); };
      img.onerror = function () {
        alert("We couldn't read that image file. Please try a different JPG or PNG.");
        if (input) input.value = "";
      };
      img.src = URL.createObjectURL(file);
    }

    zone._applyFile = applyFile; /* used when restoring a design from the cart */

    if (btn && input) {
      btn.addEventListener("click", function () { input.click(); });
      input.addEventListener("change", function () {
        if (input.files && input.files[0]) applyFile(input.files[0]);
      });
    }
    if (removeBtn) {
      removeBtn.addEventListener("click", function () {
        if (input) input.value = "";
        applyFile(null);
      });
    }
    zone.addEventListener("dragover", function (e) { e.preventDefault(); zone.classList.add("is-drag"); });
    zone.addEventListener("dragleave", function () { zone.classList.remove("is-drag"); });
    zone.addEventListener("drop", function (e) {
      e.preventDefault();
      zone.classList.remove("is-drag");
      if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]) {
        applyFile(e.dataTransfer.files[0]);
      }
    });
  });

  if (approveBox) {
    approveBox.addEventListener("change", function () {
      state.approved = approveBox.checked;
      render();
    });
  }
  if (tplApproveBox) {
    tplApproveBox.addEventListener("change", function () {
      state.tplApproved = tplApproveBox.checked;
      render();
    });
  }
  /* Back side: "same as front" checkbox + its own visual design grid */
  function buildBackGrid() {
    if (!backGrid || backGrid.childElementCount) return;
    Object.keys(REG).forEach(function (id) {
      var tpl = REG[id];
      var tile = document.createElement("div");
      tile.className = "pdp-tpl";
      tile.setAttribute("data-back-tpl", id);
      tile.innerHTML =
        '<div class="pdp-tpl__frame"><canvas class="pdp-tpl__cv" data-back-tpl-canvas="' + id + '"></canvas></div>' +
        '<div class="pdp-tpl__body"><span class="pdp-tpl__name"></span></div>' +
        '<span class="pdp-tpl__stamp">SELECTED</span>';
      tile.querySelector(".pdp-tpl__name").textContent = tpl.name || "Design";
      tile.addEventListener("click", function () {
        state.backTemplateId = id;
        var bt = REG[id];
        if (bt && !bt.drawn) overlayImage(bt.img, queuePreview);
        state.previewSide = "back"; /* show them what they just picked */
        touchDesign();
        render();
        queuePreview();
      });
      backGrid.appendChild(tile);
    });
    drawBackTiles();
  }
  function drawBackTiles() {
    root.querySelectorAll("[data-back-tpl-canvas]").forEach(function (cv) {
      drawTileCanvas(cv, REG[String(cv.getAttribute("data-back-tpl-canvas"))]);
    });
  }
  if (backSameBox) {
    backSameBox.addEventListener("change", function () {
      state.backSame = backSameBox.checked;
      if (state.backSame) {
        state.backTemplateId = null;
        state.designBack = null;
        if (state.previewSide === "back") state.previewSide = "front";
      } else {
        /* customize: back starts as a copy of the front (art + colors) */
        state.designBack = { bg: state.design.bg, textColor: state.design.textColor,
                             ink: state.design.ink };
        if (!state.backTemplateId) state.backTemplateId = String(state.templateId);
        state.previewSide = "back";
        buildBackGrid();
      }
      touchDesign();
      syncSwatches();
      render();
      queuePreview();
    });
  }
  if (sideTabs) {
    sideTabs.querySelectorAll("[data-side-tab]").forEach(function (tab) {
      tab.addEventListener("click", function () {
        state.previewSide = tab.getAttribute("data-side-tab");
        syncSwatches();
        render();
        queuePreview();
      });
    });
  }

  /* Second upload zone follows the sides choice */
  function syncUploadZones() {
    var zone2 = root.querySelector('[data-uploadzone="1"]');
    if (zone2) zone2.hidden = !isDouble();
    var panel2 = root.querySelector('[data-uz-preflight="1"]');
    if (panel2) panel2.hidden = !isDouble() || !(state.uploads[1] && state.uploads[1].img);
    var zone1Label = root.querySelector('[data-uploadzone="0"] [data-uz-label]');
    if (zone1Label) zone1Label.textContent = isDouble() ? "Side 1 · Front" : "Front";
  }
  root.querySelectorAll('[data-option="sides"]').forEach(function (btn) {
    btn.addEventListener("click", syncUploadZones);
  });

  /* ── PRINT FILE GENERATION ────────────────────────────────────────── */
  function canvasToJpeg(canvas) {
    return new Promise(function (resolve, reject) {
      canvas.toBlob(function (blob) {
        if (!blob) { reject(new Error("toBlob failed")); return; }
        blob.arrayBuffer().then(function (buf) {
          resolve({ jpeg: new Uint8Array(buf), pxW: canvas.width, pxH: canvas.height });
        }, reject);
      }, "image/jpeg", 0.9);
    });
  }

  function renderPrintPage(drawFn) {
    var spec = shapeSpec();
    var b = CFG.print.bleedIn;
    var ppi = CFG.print.dpi;
    var cv = document.createElement("canvas");
    cv.width = Math.round((spec.wIn + 2 * b) * ppi);
    cv.height = Math.round((spec.hIn + 2 * b) * ppi);
    drawFn(cv.getContext("2d"), ppi);
    return canvasToJpeg(cv);
  }

  /* One single-page PDF per printed side (production wants separate files). */
  function generatePdfs() {
    var spec = shapeSpec();
    var b = CFG.print.bleedIn;

    function buildSide(side, pagePromise) {
      return pagePromise.then(function (page) {
        return {
          side: side,
          blob: window.SignPDF.build({
            trimWIn: spec.wIn, trimHIn: spec.hIn, bleedIn: b,
            pages: [page],
            marks: {
              enabled: CFG.print.marks.enabled,
              squareIn: CFG.print.marks.squareIn,
              dotIn: CFG.print.marks.dotIn,
              positions: CFG.print.marks.positions[state.shape] || CFG.print.marks.positions.square
            },
            cropTicks: CFG.print.cropTicks,
            title: "Church Sign Shop — " + configStr() + " — " + side.toUpperCase(),
            subject: designJson()
          })
        };
      });
    }

    return ST.loadFonts().then(function () {
      var jobs = [];
      if (state.method === "template") {
        jobs.push(buildSide("front", renderPrintPage(function (ctx, ppi) { drawFullSheet(ctx, ppi, false, "front"); })));
        if (isDouble()) {
          jobs.push(buildSide("back", renderPrintPage(function (ctx, ppi) { drawFullSheet(ctx, ppi, false, "back"); })));
        }
      } else {
        jobs.push(buildSide("front", renderPrintPage(function (ctx, ppi) {
          ST.renderUpload({ ctx: ctx, img: state.uploads[0].img, shape: state.shape, bleedIn: b, ppi: ppi, guides: false });
        })));
        if (isDouble()) {
          jobs.push(buildSide("back", renderPrintPage(function (ctx, ppi) {
            ST.renderUpload({ ctx: ctx, img: state.uploads[1].img, shape: state.shape, bleedIn: b, ppi: ppi, guides: false });
          })));
        }
      }
      return Promise.all(jobs);
    });
  }

  /* Cart thumbnails: the finished sign (trim crop, rounded corners) as a
     small transparent PNG per distinct printed side. */
  var PREVIEW_PX = 600;
  function backLooksDifferent() {
    if (!isDouble()) return false;
    return state.method === "upload" ? true : backActive();
  }
  function renderPreviewPng(side) {
    var spec = shapeSpec();
    var cv = document.createElement("canvas");
    ST.renderPreviewInto(cv, PREVIEW_PX * Math.min(1, spec.wIn / spec.hIn), {
      shape: state.shape,
      bleedIn: CFG.print.bleedIn,
      cornerRadiusIn: CFG.print.cornerRadiusIn,
      dpr: 1,
      drawFull: function (ctx, ppi) {
        if (state.method === "template") {
          drawFullSheet(ctx, ppi, false, side);
        } else {
          ST.renderUpload({ ctx: ctx, img: state.uploads[side === "back" ? 1 : 0].img,
                            shape: state.shape, bleedIn: CFG.print.bleedIn, ppi: ppi, guides: false });
        }
      }
    });
    return new Promise(function (resolve) {
      cv.toBlob(function (blob) { resolve(blob); }, "image/png");
    });
  }
  function generatePreviews() {
    var sides = backLooksDifferent() ? ["front", "back"] : ["front"];
    return Promise.all(sides.map(function (side) {
      return renderPreviewPng(side).then(function (blob) { return { side: side, blob: blob }; });
    }));
  }

  function attachPdf(blob, input, name) {
    return attachFile(blob, input, name, "application/pdf");
  }
  function attachFile(blob, input, name, type) {
    if (!input || !blob || typeof DataTransfer === "undefined") return false;
    try {
      var file = new File([blob], name, { type: type });
      var dt = new DataTransfer();
      dt.items.add(file);
      input.disabled = false;
      input.files = dt.files;
      return input.files.length === 1;
    } catch (e) {
      console.warn("Could not attach print file:", e);
      return false;
    }
  }

  /* ── SAVED DESIGNS (cart "Edit design") ─────────────────────────────
     On add-to-cart the full editable state (incl. logo + uploaded files) is
     saved in this browser's IndexedDB under the line's Job code. The cart's
     EDIT DESIGN link reopens the configurator with ?edit=<job>&key=<line>,
     restores the design, and re-adding replaces that cart line.          */
  var DRAFT_DB = "cs-sign-designs", DRAFT_STORE = "designs", DRAFT_KEEP = 25;
  function draftDb() {
    return new Promise(function (resolve, reject) {
      if (!window.indexedDB) { reject(new Error("IndexedDB unavailable")); return; }
      var req = indexedDB.open(DRAFT_DB, 1);
      req.onupgradeneeded = function () { req.result.createObjectStore(DRAFT_STORE, { keyPath: "job" }); };
      req.onsuccess = function () { resolve(req.result); };
      req.onerror = function () { reject(req.error); };
    });
  }
  function saveDraft(job) {
    var rec = {
      job: job, savedAt: Date.now(),
      sides: state.sides, shape: state.shape, method: state.method, quantity: qty(),
      templateId: state.templateId, backSame: state.backSame, backTemplateId: state.backTemplateId,
      design: JSON.parse(JSON.stringify(state.design)),
      designBack: state.designBack ? JSON.parse(JSON.stringify(state.designBack)) : null,
      logoFile: state.logoImg ? state.logoFile : null, logoScale: state.logoScale,
      uploads: [0, 1].map(function (i) { var u = state.uploads[i]; return u && u.file ? u.file : null; })
    };
    return draftDb().then(function (db) {
      return new Promise(function (resolve) {
        var tx = db.transaction(DRAFT_STORE, "readwrite");
        var store = tx.objectStore(DRAFT_STORE);
        store.put(rec);
        var all = store.getAll();
        all.onsuccess = function () {
          all.result.sort(function (a, b) { return b.savedAt - a.savedAt; })
            .slice(DRAFT_KEEP).forEach(function (old) { store.delete(old.job); });
        };
        tx.oncomplete = function () { db.close(); resolve(); };
        tx.onerror = tx.onabort = function () { db.close(); resolve(); };
      });
    });
  }
  function loadDraft(job) {
    return draftDb().then(function (db) {
      return new Promise(function (resolve) {
        var req = db.transaction(DRAFT_STORE, "readonly").objectStore(DRAFT_STORE).get(job);
        req.onsuccess = function () { db.close(); resolve(req.result || null); };
        req.onerror = function () { db.close(); resolve(null); };
      });
    }).catch(function () { return null; });
  }

  function clickOption(opt, val) {
    var b = root.querySelector('[data-option="' + opt + '"][data-value="' + val + '"]');
    if (b && !b.classList.contains("is-selected")) b.click();
  }
  function setQuantity(n) {
    n = parseInt(n, 10);
    if (!n || n < 1) return;
    if (root.querySelector('[data-qty="' + n + '"]')) {
      state.customMode = false;
      state.quantity = n;
    } else {
      state.customMode = true;
      state.customValue = String(n);
      var input = root.querySelector("[data-qty-custominput]");
      if (input) input.value = String(n);
    }
  }
  var editNotice = root.querySelector("[data-edit-notice]");
  function showEditNotice(title, text, warn) {
    if (!editNotice) return;
    var t = editNotice.querySelector("[data-edit-notice-title]");
    var x = editNotice.querySelector("[data-edit-notice-text]");
    if (t) t.textContent = title;
    if (x) x.textContent = text;
    editNotice.classList.toggle("pdp-editnote--warn", !!warn);
    editNotice.hidden = false;
  }
  /* bring the customer to their design instead of leaving them at the photo */
  function scrollToEdit() {
    var target = state.method === "upload" ? stepEls.upload : (stepEls.editor || stepEls.templates);
    if (target && target.hidden) target = null;
    /* park the notice right above the step they'll work in, then scroll there */
    if (target && editNotice && target.parentNode) target.parentNode.insertBefore(editNotice, target);
    var anchor = editNotice || target;
    if (anchor && anchor.scrollIntoView) {
      setTimeout(function () { anchor.scrollIntoView({ behavior: "smooth", block: "start" }); }, 250);
    }
  }
  function restoreDraft(rec) {
    if (rec.sides) clickOption("sides", rec.sides);
    if (rec.shape) clickOption("shape", rec.shape);
    if (rec.method) clickOption("method", rec.method);
    if (rec.method === "template" && REG[String(rec.templateId)]) {
      selectTemplate(rec.templateId);
      if (rec.design) {
        state.design = rec.design;
        if (!state.design.fields) state.design.fields = {};
      }
      if (!rec.backSame && rec.designBack && REG[String(rec.backTemplateId)]) {
        state.backSame = false;
        state.designBack = rec.designBack;
        state.backTemplateId = String(rec.backTemplateId);
        var bt = REG[state.backTemplateId];
        if (bt && !bt.drawn) overlayImage(bt.img, queuePreview);
        buildBackGrid();
      }
      state.logoScale = rec.logoScale || 1;
      if (rec.logoFile) {
        var img = new Image();
        img.onload = function () {
          state.logoImg = img;
          state.logoFile = rec.logoFile;
          if (logoName) logoName.textContent = rec.logoFile.name || "Logo";
          if (logoRemove) logoRemove.hidden = false;
          syncLogoScaleUI();
          queuePreview();
        };
        img.src = URL.createObjectURL(rec.logoFile);
      }
      buildEditor();
    } else if (rec.method === "upload") {
      (rec.uploads || []).forEach(function (file, i) {
        var zone = root.querySelector('[data-uploadzone="' + i + '"]');
        if (file && zone && zone._applyFile) zone._applyFile(file);
      });
    }
    if (rec.quantity) setQuantity(rec.quantity);
    syncSwatches();
    syncLogoScaleUI();
    syncUploadZones();
    render();
    queuePreview();
  }

  /* Editing: drop the original cart line so re-adding doesn't duplicate it. */
  function removeEditedLine() {
    if (!state.editKey || !window.fetch) return Promise.resolve();
    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    return fetch(base + "cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify({ id: state.editKey, quantity: 0 })
    }).catch(function () { /* line already gone — just add the new one */ });
  }

  /* ── ADD TO CART ──────────────────────────────────────────────────── */
  var hasForm = cartForm && cartForm.tagName === "FORM";

  /* Short per-order-line job code: names the print files AND shows on the
     order line, so downloaded files match orders at a glance. */
  function makeJobCode() {
    var chars = "ABCDEFGHJKMNPQRSTUVWXYZ23456789"; /* no lookalikes */
    var out = "";
    var rnd = new Uint8Array(6);
    (window.crypto || window.msCrypto).getRandomValues(rnd);
    for (var i = 0; i < 6; i++) out += chars[rnd[i] % chars.length];
    return out;
  }

  function submitWithPrintFile() {
    if (!ready() || !hasForm) return;
    state.generating = true;
    render();
    var job = makeJobCode();
    if (props["Job"]) {
      props["Job"].value = job;
      props["Job"].disabled = false;
    }
    /* Watchdog: never leave the customer stuck on a hung generation — after
       30s submit anyway; the _Design JSON lets staff re-render the file. */
    var timedOut = false;
    var watchdog = new Promise(function (resolve) {
      setTimeout(function () { timedOut = true; resolve(null); }, 30000);
    });
    var filesOk = false;
    if (previewFileFront) previewFileFront.disabled = true;
    if (previewFileBack) previewFileBack.disabled = true;
    /* previews are nice-to-have: a failure never blocks the order */
    var previewsP = generatePreviews().then(function (list) {
      list.forEach(function (p) {
        var input = p.side === "back" ? previewFileBack : previewFileFront;
        attachFile(p.blob, input, job + "-" + state.shape + "-" + p.side + "-preview.png", "image/png");
      });
    }).catch(function (err) { console.warn("Cart preview failed:", err); });
    Promise.race([Promise.all([generatePdfs(), previewsP]).then(function (r) { return r[0]; }), watchdog])
      .then(function (results) {
        if (!results) {
          if (props["printStatus"]) {
            props["printStatus"].value = "generation timed out — re-render from _Design JSON";
          }
          return;
        }
        if (printFileBack) printFileBack.disabled = true; /* stays off unless a back file lands */
        var ok = true;
        results.forEach(function (r) {
          var input = r.side === "back" ? printFileBack : printFileFront;
          var name = job + "-" + state.shape + "-" + r.side + ".pdf";
          if (!attachPdf(r.blob, input, name)) ok = false;
        });
        filesOk = ok;
        if (!ok && props["printStatus"]) {
          props["printStatus"].value = "browser could not attach PDF — re-render from _Design JSON";
        }
      })
      .catch(function (err) {
        console.error("Print file generation failed:", err);
        if (props["printStatus"]) {
          props["printStatus"].value = "generation failed — re-render from _Design JSON";
        }
      })
      .then(function () {
        state.generating = false;
        syncFormProps();
        /* keep the order tidy: the design JSON already rides inside each
           PDF's metadata, so only submit it (and the status note) when
           something went wrong and staff need to re-render */
        if (props["design"]) props["design"].disabled = filesOk;
        if (props["printStatus"]) props["printStatus"].disabled = !props["printStatus"].value;
        /* save for EDIT DESIGN + clear the edited line; never hold up the order */
        var saved = saveDraft(job).catch(function (err) { console.warn("Design not saved for editing:", err); });
        var pre = Promise.all([saved, removeEditedLine()]);
        var cap = new Promise(function (resolve) { setTimeout(resolve, 4000); });
        return Promise.race([pre, cap]);
      })
      .then(function () {
        if (typeof cartForm.requestSubmit === "function") cartForm.requestSubmit();
        else cartForm.submit();
      });
  }

  if (hasForm) {
    cartForm.addEventListener("submit", function (e) {
      /* Only submit through the sticky button's async pipeline. */
      if (!ready() && !state.generating) e.preventDefault();
    });
  }
  if (addBtn) {
    addBtn.addEventListener("click", function () {
      if (state.generating) return;
      submitWithPrintFile();
    });
  }

  /* ── INIT ─────────────────────────────────────────────────────────── */
  syncUploadZones();
  buildFrontGrid();
  render();
  ST.loadFonts().then(function () {
    drawTemplateTiles();
    queuePreview();
  });
  window.addEventListener("resize", queuePreview);

  /* Deep link: /products/custom-church-welcome-sign?design=<handle> preselects the
     template path + that design (used by collection/home tiles + SEO pages). */
  try {
    var params = new URLSearchParams(window.location.search || "");
    var want = params.get("design");
    /* optional shape + sides preselects: ?shape=square|rectangle|circle&sides=single|double */
    var wantShape = (params.get("shape") || "").toLowerCase();
    if (ST.SHAPES[wantShape]) {
      var shapeBtn = root.querySelector('[data-option="shape"][data-value="' + wantShape + '"]');
      if (shapeBtn) shapeBtn.click();
    }
    var wantSides = (params.get("sides") || "").toLowerCase();
    if (wantSides === "single" || wantSides === "double") {
      var sidesBtn = root.querySelector('[data-option="sides"][data-value="' + wantSides + '"]');
      if (sidesBtn) sidesBtn.click();
    }
    var editJob = params.get("edit");
    if (params.get("key")) state.editKey = params.get("key");
    if (params.get("qty")) setQuantity(params.get("qty"));
    if (!want && params.get("tname")) {
      var tname = params.get("tname").toLowerCase();
      Object.keys(REG).forEach(function (id) {
        if (!want && String(REG[id].name || "").toLowerCase() === tname) want = id;
      });
    }
    if (editJob || state.editKey) {
      loadDraft(editJob || "").then(function (rec) {
        if (rec) {
          if (params.get("qty")) rec.quantity = params.get("qty");
          restoreDraft(rec);
          showEditNotice("Editing your sign.",
            "Make your changes below, tick the approval box again, then hit UPDATE CART.");
        } else {
          var m = (params.get("method") || "").toLowerCase();
          if (m === "upload" || m === "template") clickOption("method", m);
          showEditNotice("We couldn\u2019t find your saved edits on this device.",
            (state.method === "upload"
              ? "We restored your shape, sides and quantity, but you\u2019ll need to upload your artwork file again."
              : "We restored your design, shape, sides and quantity, but any colors, hashtag or logo " +
                "will need to be added again.") +
            " Tick the approval box and hit UPDATE CART when you\u2019re done.", true);
        }
        scrollToEdit();
      });
    }
    render();
    /* ?method=upload (from "upload your own" links) opens straight on the upload step */
    if (!want && !editJob && !state.editKey && (params.get("method") || "").toLowerCase() === "upload") {
      var upBtn = root.querySelector('[data-option="method"][data-value="upload"]');
      if (upBtn) upBtn.click();
    }
    if (want && REG[String(want)]) {
      var methodBtn = root.querySelector('[data-option="method"][data-value="template"]');
      if (methodBtn) methodBtn.click();
      selectTemplate(want);
      /* no auto-scroll: the customer lands at the top and reads down through
         Sides / Shape (already set) to their preselected design */
    }
  } catch (e) { /* ignore */ }
}

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initConfigurator);
  } else {
    initConfigurator();
  }
  /* Theme editor: section HTML was just replaced — bind to the fresh DOM. */
  document.addEventListener("shopify:section:load", function () {
    initConfigurator();
  });
})();
